/* AccordionGallery — React Bits (JS-CSS variant), ported to a dependency-free custom element.
 * Source: https://reactbits.dev/r/AccordionGallery-JS-CSS.json
 * Upstream animates with GSAP; this project has no bundler, so the same layout maths
 * (expandRatio grow, tilt, parallax drift, grayscale, staggered labels) runs on CSS
 * transitions instead. Markup, class names and CSS are kept as published.
 *
 * <accordion-gallery default-index="2" expand-ratio="0.52" trigger="hover"
 *                    height="460" gap="10" radius="16" accent-color="#A5D64A"
 *                    overlay-color="#0A2E45" items='[{"image":"…","label":"…","link":"#"}]'>
 */
(function () {
  'use strict';
  if (customElements.get('accordion-gallery')) return;

  var CSS = `
accordion-gallery { display:block; }
.ag-root { --ag-accent:#ffffff; --ag-overlay:#060010; --ag-text:#ffffff; --ag-gap:10px; --ag-radius:16px; --ag-media-size:320px;
  display:flex; flex-direction:row; gap:var(--ag-gap); width:100%; max-width:100%; perspective:1400px; perspective-origin:50% 50%; }
.ag-root--vertical { flex-direction:column; perspective:none; height:auto !important; }
.ag-panel { position:relative; flex:1 1 0; min-width:0; min-height:0; overflow:hidden; border-radius:var(--ag-radius); cursor:pointer;
  display:block; text-decoration:none; outline:none; transform-style:preserve-3d; transform-origin:center center; background:#0a0713;
  box-shadow:0 10px 30px -18px rgba(0,0,0,0.8); will-change:flex-grow, height, transform; -webkit-tap-highlight-color:transparent;
  transition:flex-grow .6s cubic-bezier(.16,1,.3,1), height .6s cubic-bezier(.16,1,.3,1), transform .6s cubic-bezier(.16,1,.3,1), box-shadow .6s cubic-bezier(.16,1,.3,1); }
.ag-panel:focus-visible { box-shadow:0 0 0 2px var(--ag-accent), 0 10px 30px -18px rgba(0,0,0,0.8); }
.ag-panel__frame { position:absolute; inset:0; overflow:hidden; border-radius:inherit; }
.ag-panel__media { --ag-gray:1; --ag-dim:0.35; position:absolute; top:50%; left:50%; width:var(--ag-media-size); height:100%;
  filter:grayscale(var(--ag-gray)); will-change:transform, filter;
  transition:transform .6s cubic-bezier(.16,1,.3,1), filter .6s cubic-bezier(.16,1,.3,1); }
.ag-root--vertical .ag-panel { flex:0 0 auto; min-height:88px; transform:none !important; }
.ag-root--vertical .ag-panel__media { width:100%; height:var(--ag-media-size); }
.ag-panel__media img { width:100%; height:100%; object-fit:cover; display:block; user-select:none; -webkit-user-drag:none; }
.ag-panel__overlay { position:absolute; inset:0; pointer-events:none; transition:opacity .6s cubic-bezier(.16,1,.3,1);
  background:linear-gradient(180deg, transparent 45%, color-mix(in srgb, var(--ag-overlay) 78%, transparent) 100%),
             color-mix(in srgb, var(--ag-overlay) calc(var(--ag-dim, 0.35) * 100%), transparent); }
.ag-panel__label { position:absolute; left:20px; bottom:20px; right:20px; display:flex; align-items:center; gap:12px; pointer-events:none; z-index:2; }
.ag-panel__bar { flex:0 0 auto; width:3px; height:26px; border-radius:3px; background:var(--ag-accent); opacity:0;
  box-shadow:0 0 12px color-mix(in srgb, var(--ag-accent) 60%, transparent);
  transition:opacity .6s cubic-bezier(.16,1,.3,1), transform .6s cubic-bezier(.16,1,.3,1); }
.ag-panel__text { color:var(--ag-text); font-family:inherit; font-weight:600; font-size:clamp(1rem,1.4vw,1.4rem); letter-spacing:0.01em;
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis; opacity:0; text-shadow:0 2px 14px rgba(0,0,0,0.55);
  transition:opacity .6s cubic-bezier(.16,1,.3,1) .06s, transform .6s cubic-bezier(.16,1,.3,1) .06s; }
@media (prefers-reduced-motion:reduce) {
  .ag-panel, .ag-panel__media, .ag-panel__bar, .ag-panel__text { will-change:auto; transition:none !important; }
}`;

  if (!document.getElementById('accordion-gallery-css')) {
    var st = document.createElement('style');
    st.id = 'accordion-gallery-css';
    st.textContent = CSS;
    document.head.appendChild(st);
  }

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var num = function (v, d) { var n = parseFloat(v); return isNaN(n) ? d : n; };
  var mq = window.matchMedia('(max-width:720px)');

  class AccordionGallery extends HTMLElement {
    connectedCallback() {
      if (this._built) return;
      this._built = true;

      try { this.items = JSON.parse(this.getAttribute('items') || '[]'); }
      catch (err) { this.items = []; }
      if (!this.items.length) return;

      this.expandRatio = num(this.getAttribute('expand-ratio'), 0.52);
      this.gap = num(this.getAttribute('gap'), 10);
      this.radius = num(this.getAttribute('radius'), 16);
      this.height = num(this.getAttribute('height'), 460);
      this.tilt = num(this.getAttribute('tilt'), 8);
      this.parallax = num(this.getAttribute('parallax'), 0.5);
      this.trigger = this.getAttribute('trigger') || 'hover';
      this.grayscale = this.getAttribute('grayscale') !== 'false';
      this.active = Math.min(Math.max(num(this.getAttribute('default-index'), 2), 0), this.items.length - 1);
      this.mediaSize = 320;
      this.collapsedHeight = 92;
      this.expandHeight = 280;

      var root = document.createElement('div');
      root.className = 'ag-root';
      root.setAttribute('role', 'list');
      root.setAttribute('aria-label', this.getAttribute('aria-label') || 'Image gallery');
      root.style.setProperty('--ag-accent', this.getAttribute('accent-color') || '#ffffff');
      root.style.setProperty('--ag-overlay', this.getAttribute('overlay-color') || '#060010');
      root.style.setProperty('--ag-text', this.getAttribute('text-color') || '#ffffff');
      root.style.setProperty('--ag-gap', this.gap + 'px');
      root.style.setProperty('--ag-radius', this.radius + 'px');
      root.style.height = this.height + 'px';
      this.appendChild(root);
      this.root = root;

      this.panels = this.items.map(function (item, i) {
        var tag = item.link ? 'a' : 'div';
        var el = document.createElement(tag);
        el.className = 'ag-panel';
        if (item.link) el.href = item.link;
        el.setAttribute('role', 'listitem');
        el.tabIndex = 0;
        el.setAttribute('aria-label', item.label || '');
        el.innerHTML =
          '<span class="ag-panel__frame">' +
            '<span class="ag-panel__media"><img src="' + item.image + '" alt="' + (item.alt || item.label || '') + '" draggable="false"></span>' +
            '<span class="ag-panel__overlay" aria-hidden="true"></span>' +
          '</span>' +
          '<span class="ag-panel__label" aria-hidden="true">' +
            '<span class="ag-panel__bar"></span>' +
            '<span class="ag-panel__text">' + (item.label || '') + '</span>' +
          '</span>';
        this.root.appendChild(el);
        return el;
      }, this);

      var self = this;
      this.panels.forEach(function (el, i) {
        el.addEventListener('mouseenter', function () { if (!self.isVertical() && self.trigger === 'hover') self.setActive(i); });
        el.addEventListener('focus', function () { self.setActive(i); });
        el.addEventListener('click', function (e) { if (i !== self.active) { e.preventDefault(); self.setActive(i); } });
        el.addEventListener('keydown', function (e) {
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); self.setActive((i + 1) % self.items.length); }
          else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); self.setActive((i - 1 + self.items.length) % self.items.length); }
        });
      });

      this._onScroll = function () {
        if (self._scrollTick) return;
        self._scrollTick = true;
        requestAnimationFrame(function () {
          self._scrollTick = false;
          self.syncFromScroll();
        });
      };
      window.addEventListener('scroll', this._onScroll, { passive: true });
      if (mq.addEventListener) mq.addEventListener('change', function () { self.measure(); });
      else if (mq.addListener) mq.addListener(function () { self.measure(); });

      this._ro = new ResizeObserver(function () { self.measure(); });
      this._ro.observe(this.root);
      this.measure();
      this._mo = new MutationObserver(function () { self.layout(); });
      this._mo.observe(this, { attributes: true, attributeFilter: ['style', 'class'] });
      requestAnimationFrame(function () { self.measure(); self.syncFromScroll(); });
    }

    disconnectedCallback() {
      if (this._ro) this._ro.disconnect();
      if (this._mo) this._mo.disconnect();
      if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
    }

    isVertical() { return mq.matches; }

    setActive(i) { if (i === this.active) return; this.active = i; this.layout(); }

    syncFromScroll() {
      if (!this.isVertical() || !this.panels) return;
      var mid = window.innerHeight * 0.42;
      var best = this.active;
      var bestDist = Infinity;
      this.panels.forEach(function (el, i) {
        var r = el.getBoundingClientRect();
        if (r.bottom < 0 || r.top > window.innerHeight) return;
        var c = r.top + r.height / 2;
        var d = Math.abs(c - mid);
        if (d < bestDist) { bestDist = d; best = i; }
      });
      this.setActive(best);
    }

    measure() {
      var vertical = this.isVertical();
      var rect = this.root.getBoundingClientRect();
      var count = this.items.length;
      if (vertical) this.root.classList.add('ag-root--vertical');
      else this.root.classList.remove('ag-root--vertical');

      if (vertical) {
        this.collapsedHeight = 92;
        this.expandHeight = Math.min(Math.max(Math.round(rect.width * 0.82), 220), 380);
        this.mediaSize = Math.max(180, rect.width);
        this.root.style.height = 'auto';
      } else {
        var usable = Math.max(rect.width - this.gap * (count - 1), 120);
        this.mediaSize = Math.max(140, usable * Math.min(Math.max(this.expandRatio, 0.2), 0.9) * 1.22);
        this.root.style.height = this.height + 'px';
      }
      this.root.style.setProperty('--ag-media-size', this.mediaSize + 'px');
      this.layout();
    }

    layout() {
      var count = this.items.length;
      var r = Math.min(Math.max(this.expandRatio, 0.2), 0.9);
      var grow = count > 1 ? (r * (count - 1)) / (1 - r) : 1;
      var vertical = this.isVertical();
      var self = this;

      this.panels.forEach(function (panel, i) {
        var isActive = i === self.active;
        var media = panel.querySelector('.ag-panel__media');
        var bar = panel.querySelector('.ag-panel__bar');
        var text = panel.querySelector('.ag-panel__text');
        var rot = isActive ? 0 : (i < self.active ? self.tilt : -self.tilt);

        if (vertical) {
          panel.style.flexGrow = '0';
          panel.style.height = (isActive ? self.expandHeight : self.collapsedHeight) + 'px';
          panel.style.transform = 'none';
        } else {
          panel.style.height = '';
          panel.style.flexGrow = isActive ? grow : 1;
          panel.style.transform = reduce ? 'none' : 'rotateY(' + rot + 'deg)';
        }
        panel.setAttribute('aria-current', isActive ? 'true' : 'false');

        if (media) {
          var drift = Math.max(-1.5, Math.min(1.5, self.active - i));
          var shift = drift * self.parallax * self.mediaSize * 0.06;
          media.style.transform = 'translate(-50%,-50%) translateX(' + (isActive ? 0 : shift) + 'px)';
          media.style.setProperty('--ag-gray', self.grayscale ? (isActive ? 0 : 1) : 0);
          media.style.setProperty('--ag-dim', isActive ? 0 : 0.35);
        }
        if (bar && text) {
          bar.style.opacity = isActive ? 1 : 0;
          text.style.opacity = isActive ? 1 : 0;
          bar.style.transform = isActive ? 'none' : 'translateX(-14px)';
          text.style.transform = isActive ? 'none' : 'translateX(-14px)';
        }
      });
    }
  }

  customElements.define('accordion-gallery', AccordionGallery);
})();
